import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';

import { useIntl } from 'ha/i18n';
import { Typography, Grid, Text, units } from '@hbf/dsl';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { TypeFilter } from 'ha/components/Filters/TypeFilter';
import { FurnitureFilter } from 'ha/components/Filters/FurnitureFilter';
import { SuitableForFilter } from 'ha/components/Filters/SuitableForFilter';
import { GenderFilter } from 'ha/components/Filters/GenderFilter';
import { RegistrationFilter } from 'ha/components/Filters/RegistrationFilter';
import { LocationFilter } from 'ha/components/Filters/LocationFilter';
import { PropertyTypeFilter as PropertySizeFilter } from 'ha/components/Filters/PropertySizeFilter';
import { ContractTypeFilter } from 'ha/components/Filters/ContractTypeFilter';
import { PriceAndBillsFilter } from 'ha/components/Filters/PriceAndBillsFilter';
import { FacilitiesFilter } from 'ha/components/Filters/FacilitiesFilter';
import { AmenitiesFilter } from 'ha/components/Filters/AmenitiesFilter';
import { FilterAccordion } from 'ha/components/Filters/FilterAccordion';
import { useSearchBarContext } from 'ha/modules/SearchBar/SearchBarContext';
import {
  showRegistration,
  getLocalRegistrationTranslationByCountry,
  getLocalRegistrationDescriptionTranslationByCountry,
} from 'ha/utils/filters/registration';
import { Experiments } from 'ha/constants/experiments';
import { Experiment, Variant } from 'ha/modules/Experiments';
import { dateRangeToAvailability } from '@ha/date';

import { SearchFiltersContext, EmptyState } from '../../SearchFiltersContext';

import {
  getIsContractTypeFilterHidden,
  getIsFurnitureFilterHidden,
  getIsGenderFilterHidden,
  getIsRegistrationFilterHidden,
  getIsSuitableForFilterHidden,
  getPropertySizeStats,
  getIsFacilitiesFilterHidden,
  getIsAmenitiesFilterHidden,
} from '../../selectors/filterSelectors';
import { SearchFiltersTooltip } from './SearchFiltersTooltip';
import { SearchDateRangePicker } from './SearchDateRangePicker';
import { getCountryCode, getIsImperialSystem } from '../../selectors';
import { SearchMoveInSelector } from './SearchMoveInSelector';
import { AvailabilityDuration } from './AvailabilityDuration';

const { useMemo } = React;

const useStyles = makeStyles()(() => ({
  tooltipTitle: {
    marginBottom: units.rem(0.25),
  },
  tooltipParagraph: {
    marginBottom: units.rem(1),
  },
}));

interface OwnProps {
  withDateFilter?: boolean;
}

const SearchFilters: React.FC<OwnProps> = ({ withDateFilter }) => {
  const { T } = useIntl();
  const { classes } = useStyles();

  const {
    searchContractTypeFilter,
    searchLocationFilters,
    flexibleSearchDates,
    rearrangeSearchFilter,
  } = useFeatureFlags();
  const isLocationEnabled =
    searchLocationFilters === 'on' && rearrangeSearchFilter !== 'on';

  const isContractTypeFilterHidden =
    useSelector(getIsContractTypeFilterHidden) ||
    searchContractTypeFilter !== 'on';
  const isFurnitureFilterHidden = useSelector(getIsFurnitureFilterHidden);
  const isGenderFilterHidden = useSelector(getIsGenderFilterHidden);
  const isRegistrationFilterHidden = useSelector(getIsRegistrationFilterHidden);
  const isSuitableForFilterHidden = useSelector(getIsSuitableForFilterHidden);
  const isFacilitiesFilterHidden = useSelector(getIsFacilitiesFilterHidden);
  const isAmenitiesFilterHidden = useSelector(getIsAmenitiesFilterHidden);
  const countryCode = useSelector(getCountryCode);
  const isImperialSystem = useSelector(getIsImperialSystem);

  const propertySizeStats = useSelector(getPropertySizeStats);

  const {
    localState,
    filtersCount,
    priceParams,
    onChangeDates,
    onChangeContractType,
    onChangeRooms,
    onChangeTypes,
    onChangePrice,
    onChangeCurrency,
    onChangeFurniture,
    onChangeBills,
    onChangeSuitableFor,
    onChangeGender,
    onChangeRegistration,
    onChangeSearchAreaFilter,
    onChangeRadius,
    onChangePropertySize,
    onChangeFacilities,
    onChangeAmenities,
    onChangePlace,
    isFilterEmpty,
    onChangeAvailability,
  } = React.useContext(SearchFiltersContext);
  const { initialSuggestions } = useSearchBarContext();

  const availabilityValues = useMemo(
    () =>
      localState.dates.startDate !== null
        ? dateRangeToAvailability({
            startDate: localState.dates.startDate.toString(),
            endDate: localState.dates.endDate?.toString(),
          })
        : null,
    [localState.dates],
  );

  const handleLocationClear = React.useCallback(() => {
    onChangeRadius(EmptyState.radius);
  }, [onChangeRadius]);

  const handlePriceAndBillsClear = React.useCallback(() => {
    onChangePrice(EmptyState.price);
    onChangeBills(EmptyState.bills);
  }, [onChangePrice, onChangeBills]);

  const handleDatesClear = React.useCallback(() => {
    onChangeDates(EmptyState.dates);
  }, [onChangeDates]);

  const handlePropertyTypeClear = React.useCallback(() => {
    onChangeTypes(EmptyState.types);
  }, [onChangeTypes]);

  const handlePropertySizeClear = React.useCallback(() => {
    onChangePropertySize(EmptyState.propertySize!);
  }, [onChangePropertySize]);

  const handleContractTypeClear = React.useCallback(() => {
    onChangeContractType(EmptyState.contractType);
  }, [onChangeContractType]);

  const handleFurnitureClear = React.useCallback(() => {
    onChangeFurniture(EmptyState.furniture);
  }, [onChangeFurniture]);

  const handleFacilitisClear = React.useCallback(() => {
    onChangeFacilities(EmptyState.facilities);
  }, [onChangeFacilities]);

  const handleAmenitiesClear = React.useCallback(() => {
    onChangeAmenities(EmptyState.amenities);
  }, [onChangeAmenities]);

  const handleSuitableForClear = React.useCallback(() => {
    onChangeSuitableFor(EmptyState.suitableFor);
    onChangeGender(EmptyState.gender);
  }, [onChangeSuitableFor, onChangeGender]);

  const handleRegistrationClear = React.useCallback(() => {
    onChangeRegistration(EmptyState.registration);
  }, [onChangeRegistration]);

  const handleMoveInDateChange = (date: string) => {
    onChangeAvailability({
      ...availabilityValues,
      moveIn: {
        type: date.endsWith(dayjs(date).daysInMonth().toString())
          ? 'month'
          : 'date',
        value: date,
      },
    });
  };
  const handleDurationChange = (duration: number) => {
    if (!availabilityValues?.moveIn.value) return;
    onChangeAvailability({ ...availabilityValues, duration });
  };

  return (
    <React.Fragment>
      {isLocationEnabled && (
        <FilterAccordion
          label={T('search.filters.location.title')}
          defaultExpanded
          dataTestLocator="Search Area"
          onClear={isFilterEmpty('radius') ? undefined : handleLocationClear}
          trackText="Search filter accordion clicked"
          topBorder
        >
          <LocationFilter
            hideReset
            placeProps={{
              value: localState.place,
              onChange: onChangePlace,
              initialItems: initialSuggestions,
              changeOnlyOnSelect: true,
            }}
            searchAreaProps={{
              value: localState.searchArea,
              onChange: onChangeSearchAreaFilter,
              location: 'filter',
            }}
            radiusProps={{
              value: localState.radius,
              onChange: onChangeRadius,
            }}
          />
        </FilterAccordion>
      )}
      <FilterAccordion
        label={T('Price')}
        defaultExpanded
        dataTestLocator="Price"
        onClear={
          isFilterEmpty('price') && isFilterEmpty('bills')
            ? undefined
            : handlePriceAndBillsClear
        }
        trackText="Search filter accordion clicked"
        topBorder
      >
        <PriceAndBillsFilter
          price={{
            value: localState.price,
            onChange: onChangePrice,
            currencyValue: localState.currency,
            onCurrencyChange: onChangeCurrency,
            ...priceParams,
          }}
          bills={{
            value: localState.bills,
            onChange: onChangeBills,
            count: filtersCount.bills,
          }}
        />
      </FilterAccordion>

      {withDateFilter && flexibleSearchDates !== 'on' && (
        <FilterAccordion
          label={T('Date')}
          defaultExpanded
          dataTestLocator="Date"
          onClear={isFilterEmpty('dates') ? undefined : handleDatesClear}
          trackText="Search filter accordion clicked"
          topBorder
        >
          <SearchDateRangePicker
            value={localState.dates}
            onChange={onChangeDates}
          />
        </FilterAccordion>
      )}

      {withDateFilter && flexibleSearchDates === 'on' && (
        <Experiment name={Experiments.TNT478.name}>
          <Variant name="A">
            <FilterAccordion
              label={T('Move in')}
              defaultExpanded
              dataTestLocator="Flexible search dates"
              trackText="Search filter accordion clicked"
              onClear={isFilterEmpty('dates') ? undefined : handleDatesClear}
              topBorder
            >
              <SearchMoveInSelector
                onChange={handleMoveInDateChange}
                onClear={handleDatesClear}
                value={availabilityValues?.moveIn.value}
              />
              <AvailabilityDuration
                onChange={handleDurationChange}
                duration={availabilityValues?.duration}
              />
            </FilterAccordion>
          </Variant>
          <Variant name="B">
            <FilterAccordion
              label={T('Date')}
              defaultExpanded
              dataTestLocator="Date"
              onClear={isFilterEmpty('dates') ? undefined : handleDatesClear}
              trackText="Search filter accordion clicked"
              topBorder
            >
              <SearchDateRangePicker
                value={localState.dates}
                onChange={onChangeDates}
              />
            </FilterAccordion>
          </Variant>
        </Experiment>
      )}

      <FilterAccordion
        label={T('Property type')}
        defaultExpanded
        dataTestLocator="Property Type"
        onClear={isFilterEmpty('types') ? undefined : handlePropertyTypeClear}
        trackText="Search filter accordion clicked"
        topBorder
      >
        <TypeFilter
          value={localState.types}
          onChange={onChangeTypes}
          roomValue={localState.rooms}
          onChangeRooms={onChangeRooms}
          count={filtersCount.propertyType}
          roomsCount={filtersCount.rooms}
        />
      </FilterAccordion>

      {!isFurnitureFilterHidden && (
        <FilterAccordion
          label={T('Furniture')}
          trackText="Search filter accordion clicked"
          topBorder
          defaultExpanded
          dataTestLocator="Furniture"
          onClear={
            isFilterEmpty('furniture') ? undefined : handleFurnitureClear
          }
        >
          <FurnitureFilter
            value={localState.furniture}
            onChange={onChangeFurniture}
            count={filtersCount.furniture}
          />
        </FilterAccordion>
      )}

      <FilterAccordion
        label={T('search.filters.property_size.title')}
        defaultExpanded={!isFilterEmpty('propertySize')}
        dataTestLocator="Property Size"
        onClear={
          isFilterEmpty('propertySize') ? undefined : handlePropertySizeClear
        }
        trackText="Search filter accordion clicked"
        topBorder
      >
        <PropertySizeFilter
          value={localState.propertySize}
          onChange={onChangePropertySize}
          count={filtersCount.propertySize}
          stats={propertySizeStats}
          isImperialSystem={isImperialSystem}
        />
      </FilterAccordion>

      {!isContractTypeFilterHidden && (
        <FilterAccordion
          label={
            <Grid container alignItems="baseline">
              <Grid item>
                <Typography.ParagraphTitle>
                  {T('rental_contract.title')}
                </Typography.ParagraphTitle>
              </Grid>
              <Grid item>
                <SearchFiltersTooltip
                  title={
                    <Typography.H2>{T('rental_contract.title')}</Typography.H2>
                  }
                  text={
                    <React.Fragment>
                      <Typography.Caption
                        fontWeight="bold"
                        className={classes.tooltipTitle}
                      >
                        {T('rental_contract.daily')}
                      </Typography.Caption>
                      <Typography.Caption className={classes.tooltipParagraph}>
                        {T('rental_contract.daily.tooltip')}
                      </Typography.Caption>
                      <Typography.Caption
                        fontWeight="bold"
                        className={classes.tooltipTitle}
                      >
                        {T('rental_contract.fortnightly')}
                      </Typography.Caption>
                      <Typography.Caption className={classes.tooltipParagraph}>
                        {T('rental_contract.fortnightly.tooltip')}
                      </Typography.Caption>
                      <Typography.Caption
                        fontWeight="bold"
                        className={classes.tooltipTitle}
                      >
                        {T('rental_contract.monthly')}
                      </Typography.Caption>
                      <Typography.Caption>
                        {T('rental_contract.monthly.tooltip')}
                      </Typography.Caption>
                    </React.Fragment>
                  }
                  analyticsName="contractType"
                />
              </Grid>
            </Grid>
          }
          defaultExpanded={!isFilterEmpty('contractType')}
          dataTestLocator="Contract Type"
          onClear={
            isFilterEmpty('contractType') ? undefined : handleContractTypeClear
          }
          trackText="Search filter accordion clicked"
          topBorder
        >
          <ContractTypeFilter
            value={localState.contractType}
            onChange={onChangeContractType}
            count={filtersCount.contractType}
          />
        </FilterAccordion>
      )}

      {!isFacilitiesFilterHidden && (
        <FilterAccordion
          trackText="Search filter accordion clicked"
          topBorder
          label={T('Facilities')}
          defaultExpanded={!isFilterEmpty('facilities')}
          dataTestLocator="Facilities Filter"
          onClear={
            isFilterEmpty('facilities') ? undefined : handleFacilitisClear
          }
        >
          <FacilitiesFilter
            value={localState.facilities}
            onChange={onChangeFacilities}
            count={filtersCount.facilities}
          />
        </FilterAccordion>
      )}

      {!isAmenitiesFilterHidden && (
        <FilterAccordion
          trackText="Search filter accordion clicked"
          topBorder
          label={T('Amenities')}
          defaultExpanded={!isFilterEmpty('amenities')}
          dataTestLocator="Amenities Filter"
          onClear={
            isFilterEmpty('amenities') ? undefined : handleAmenitiesClear
          }
        >
          <AmenitiesFilter
            value={localState.amenities}
            onChange={onChangeAmenities}
            count={filtersCount.amenities}
          />
        </FilterAccordion>
      )}

      {(!isSuitableForFilterHidden || !isGenderFilterHidden) && (
        <FilterAccordion
          trackText="Search filter accordion clicked"
          topBorder
          label={T('Suitable for')}
          defaultExpanded={
            !isFilterEmpty('suitableFor') || !isFilterEmpty('gender')
          }
          dataTestLocator="Suitable For"
          onClear={
            isFilterEmpty('suitableFor') && isFilterEmpty('gender')
              ? undefined
              : handleSuitableForClear
          }
        >
          <Grid container direction="column">
            <Grid item>
              <SuitableForFilter
                value={localState.suitableFor}
                onChange={onChangeSuitableFor}
                count={filtersCount.suitableFor}
              />
            </Grid>
            <Grid item>
              <GenderFilter
                value={localState.gender}
                onChange={onChangeGender}
                count={filtersCount.gender}
              />
            </Grid>
          </Grid>
        </FilterAccordion>
      )}

      {showRegistration(countryCode) && !isRegistrationFilterHidden && (
        <FilterAccordion
          trackText="Search filter accordion clicked"
          topBorder
          label={
            <Grid container alignItems="baseline">
              <Grid item>
                <Typography.ParagraphTitle>
                  {T('Registration')}
                </Typography.ParagraphTitle>
              </Grid>
              <Grid item>
                <SearchFiltersTooltip
                  title={<Typography.H2>{T('Registration')}</Typography.H2>}
                  text={
                    <Typography.Caption>
                      {T(
                        getLocalRegistrationDescriptionTranslationByCountry(
                          countryCode,
                        ),
                      )}{' '}
                      <Text italic kind="inline" variant="inherit">
                        {T(
                          getLocalRegistrationTranslationByCountry(countryCode),
                        )}
                      </Text>
                    </Typography.Caption>
                  }
                  analyticsName="registration"
                />
              </Grid>
            </Grid>
          }
          defaultExpanded={!isFilterEmpty('registration')}
          dataTestLocator="Registration"
          onClear={
            isFilterEmpty('registration') ? undefined : handleRegistrationClear
          }
        >
          <RegistrationFilter
            value={localState.registration}
            onChange={onChangeRegistration}
            count={filtersCount.registration}
          />
        </FilterAccordion>
      )}
    </React.Fragment>
  );
};

export { SearchFilters };
